// src/pages/ProcurementForm.jsx
import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import { useNavigate, useParams } from "react-router-dom";
import { useProcurement } from "../../context/ProcurementContext";
import { useInventory } from "../../context/InventoryContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ProcurementForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // id from route when editing
  const { saveProcurement, getProcurement } = useProcurement();
  const { inventories } = useInventory();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    id: "",
    poNumber: "",
    projectName: "",
    vendorName: "",
    budgetAllocated: "",
    paymentTerms: "",
    expectedDelivery: "",
    status: "Pending",
    description: "",
    rfpDetails: "",
    purchaseOrderDetails: "",
    vendorNegotiations: "",
    materialDeliveryTracking: "",
    materialId: "", // link to inventory item id
    materialName: "",
    quantity: 0,
  });

  useEffect(() => {
    if (isEdit) {
      const existing = getProcurement(id);
      if (existing) {
        setFormData({
          id: String(existing.id || id),
          poNumber: existing.poNumber || "",
          projectName: existing.projectName || "",
          vendorName: existing.vendorName || "",
          budgetAllocated:
            existing.budgetAllocated === undefined || existing.budgetAllocated === null
              ? ""
              : existing.budgetAllocated,
          paymentTerms: existing.paymentTerms || "",
          expectedDelivery: existing.expectedDelivery || existing.expectedDate || "",
          status: existing.status || "Pending",
          description: existing.description || "",
          rfpDetails: existing.rfpDetails || "",
          purchaseOrderDetails: existing.purchaseOrderDetails || "",
          vendorNegotiations: existing.vendorNegotiations || "",
          materialDeliveryTracking: existing.materialDeliveryTracking || "",
          materialId: existing.materialId || existing.inventoryId || "",
          materialName: existing.materialName || "",
          quantity: existing.quantity || 0,
        });
      } else {
        toast.error("Procurement record not found for editing", { position: "top-right" });
        setTimeout(() => navigate("/admin/Procurement"), 800);
      }
    } else {
      // reset on add
      setFormData((p) => ({
        ...p,
        id: "",
        poNumber: "",
        projectName: "",
        vendorName: "",
        budgetAllocated: "",
        paymentTerms: "",
        expectedDelivery: "",
        status: "Pending",
        description: "",
        rfpDetails: "",
        purchaseOrderDetails: "",
        vendorNegotiations: "",
        materialDeliveryTracking: "",
        materialId: "",
        materialName: "",
        quantity: 0,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "budgetAllocated") {
      const cleaned = value === "" ? "" : value.replace(/[^0-9.]/g, "");
      setFormData((prev) => ({ ...prev, [name]: cleaned }));
    } else if (name === "materialId") {
      // select changed: set materialId and materialName
      const selectedId = value === "" ? "" : Number(value);
      const inv = inventories.find((it) => String(it.id) === String(selectedId));
      setFormData((prev) => ({
        ...prev,
        materialId: selectedId,
        materialName: inv ? inv.itemName || "" : "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const finalData = {
      ...formData,
      poNumber:
        (formData.poNumber || "").trim() !== ""
          ? formData.poNumber
          : `PO-${Math.floor(1000 + Math.random() * 9000)}`,
      quantity: Number(formData.quantity || 0),
    };

    if (isEdit) finalData.id = String(id);

    saveProcurement(finalData);

    toast.success(isEdit ? "✅ Procurement updated successfully!" : "🎉 Procurement added successfully!", {
      position: "top-right",
      autoClose: 1400,
      theme: "colored",
    });

    setTimeout(() => navigate("/admin/Procurement"), 900);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-white text-gray-900">
        <div className="p-6 md:p-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-base font-semibold text-gray-800 mb-4">Procurement Management</h2>

            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">PO Number</label>
                  <input
                    name="poNumber"
                    value={formData.poNumber}
                    onChange={handleChange}
                    placeholder="PO-1023"
                    className="w-full border border-black rounded-md px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Project Name</label>
                  <input
                    name="projectName"
                    value={formData.projectName}
                    onChange={handleChange}
                    required
                    placeholder="Project name"
                    className="w-full border border-black rounded-md px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Vendor Name</label>
                  <input
                    name="vendorName"
                    value={formData.vendorName}
                    onChange={handleChange}
                    placeholder="Vendor name"
                    className="w-full border border-black rounded-md px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Material</label>
                  <select
                    name="materialId"
                    value={formData.materialId || ""}
                    onChange={handleChange}
                    className="w-full border border-black rounded-md px-4 py-2 bg-white outline-none"
                  >
                    <option value="">-- Select material from inventory --</option>
                    {inventories.map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.itemName || `${it.itemName || "Item"} (${it.sku || ""})`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Quantity</label>
                  <input
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    type="number"
                    min="0"
                    placeholder="Quantity"
                    className="w-full border border-black rounded-md px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Budget Allocated</label>
                  <input
                    name="budgetAllocated"
                    value={formData.budgetAllocated}
                    onChange={handleChange}
                    placeholder="0"
                    className="w-full border border-black rounded-md px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Payment Terms</label>
                  <input
                    name="paymentTerms"
                    value={formData.paymentTerms}
                    onChange={handleChange}
                    placeholder="Payment terms"
                    className="w-full border border-black rounded-md px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Expected Delivery</label>
                  <input
                    name="expectedDelivery"
                    value={formData.expectedDelivery}
                    onChange={handleChange}
                    type="date"
                    className="w-full border border-black rounded-md px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full border border-black rounded-md px-4 py-2 bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Fulfilled">Fulfilled</option>
                    <option value="Sent">Sent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={2}
                    placeholder="Short description"
                    className="w-full border border-black rounded-md px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* extra details */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">RFP Details</label>
                  <textarea
                    name="rfpDetails"
                    value={formData.rfpDetails}
                    onChange={handleChange}
                    rows={2}
                    placeholder="RFP details"
                    className="w-full border border-black rounded-md px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Purchase Order Details</label>
                  <textarea
                    name="purchaseOrderDetails"
                    value={formData.purchaseOrderDetails}
                    onChange={handleChange}
                    rows={2}
                    placeholder="PO details"
                    className="w-full border border-black rounded-md px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Vendor Negotiations</label>
                  <textarea
                    name="vendorNegotiations"
                    value={formData.vendorNegotiations}
                    onChange={handleChange}
                    rows={2}
                    placeholder="Negotiation notes"
                    className="w-full border border-black rounded-md px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Material Delivery Tracking</label>
                  <textarea
                    name="materialDeliveryTracking"
                    value={formData.materialDeliveryTracking}
                    onChange={handleChange}
                    rows={2}
                    placeholder="Delivery tracking info"
                    className="w-full border border-black rounded-md px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              <div className="pt-2">
                <button type="submit" className="bg-gray-900 text-white px-6 py-2 rounded-md hover:bg-gray-800 transition-colors">
                  {isEdit ? "Update Procurement" : "Save Procurement"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProcurementForm;
